import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import { FormItemProps, notification } from "antd";
import axios from "axios";
import {
  AssetsEntity,
  ParticipantEntity,
} from "features/appProfileRegister/domain/entities/AppProfileExtraEntity";
import { AppProfileResponseEntity } from "features/appProfileRegister/domain/entities/AppProfileResponseEntity";
import isEmpty from "lodash/isEmpty";
import isUndefined from "lodash/isUndefined";
import { CurrencyEnum } from "utils/enums/CurrencyEnum";
import { colorFormat } from "utils/format/ColorFormat";
import { DataFormat } from "utils/format/DataFormat";
import { NumberFormat } from "utils/format/NumberFormat";
import { strings } from "utils/localizedStrings";

export const createFormRules = (
  stricted: boolean,
  dataType: string,
  message: string,
) => {
  return [
    {
      required: stricted,
      type: dataType,
      message: message,
    },
  ] as FormItemProps["rules"];
};

export const getFormInitialVals = <O extends Object>(
  data: O,
  value: keyof O,
) => {
  if (!isEmpty(data)) {
    return data[value];
  } else {
    return undefined;
  }
};

export const getMessage = (
  info: string,
  time: number,
  type: "success" | "error" | "warning" | "info",
) => {
  const defaultStyle = {
    color: "white",
    fontWeight: 500,
    borderRadius: 7,
    width: 530,
    fontSize: "15px",
  };
  notification?.destroy();
  switch (type) {
    case "info":
      return notification.info({
        message: <span style={{ ...defaultStyle }}>{info}</span>,
        icon: <InfoCircleOutlined className="text-white" />,
        closable: false,
        style: {
          backgroundColor: colorFormat.Blue,
          ...defaultStyle,
        },
      });
    case "success":
      return notification.success({
        message: <span style={{ ...defaultStyle }}>{info}</span>,
        icon: <CheckCircleOutlined className="text-white" />,
        closable: false,
        style: {
          backgroundColor: "#12B841",
          ...defaultStyle,
        },
        duration: time,
      });
    case "error":
      return notification.error({
        message: (
          <span style={{ ...defaultStyle }}>
            {info || strings.GlobalMessage.Error}
          </span>
        ),
        icon: <CloseCircleOutlined className="text-white" />,
        closable: false,
        style: {
          backgroundColor: "#E22A2A",
          ...defaultStyle,
        },
        duration: time,
      });
    case "warning":
      return notification.warning({
        message: (
          <span style={{ ...defaultStyle }}>
            {info || strings.GlobalLabels.Error}
          </span>
        ),
        icon: <InfoCircleOutlined className="text-white" />,
        closable: false,
        style: {
          backgroundColor: colorFormat?.Yellow,
          ...defaultStyle,
        },
        duration: time,
      });
  }
};

export const renderStatus = (value: unknown) => {
  switch (value) {
    case "active":
      return (
        <span className="font-semibold text-green-700">
          {strings.GlobalLabels.Active}
        </span>
      );
    case "inactive":
      return (
        <span className="font-semibold text-red-700">
          {strings.GlobalLabels.Inactive}
        </span>
      );
    case 0:
      return (
        <span className="font-semibold text-green-700">
          {strings.GlobalLabels.Active}
        </span>
      );
    case 1:
      return (
        <span className="font-semibold text-red-700">
          {strings.GlobalLabels.Inactive}
        </span>
      );
    case -1:
      return (
        <span className="font-semibold text-red-700">
          {strings.GlobalLabels.Inactive}
        </span>
      );
    case -2:
      return (
        <span className="font-semibold text-red-800">
          {strings.GlobalLabels.Deleted}
        </span>
      );
  }
};

export const getUserType = (value: string) => {
  switch (value) {
    case "user":
      return <span style={{ color: "#473b3b", fontWeight: 700 }}>User</span>;
    case "admin":
      return <span style={{ color: "#180d7a", fontWeight: 700 }}>Admin</span>;
    case "partner":
      return <span style={{ color: "#650d7a", fontWeight: 700 }}>Partner</span>;
  }
};

export const getUserStatus = (value: number) => {
  switch (value) {
    case 0:
      return (
        <span style={{ color: "#32a852", fontWeight: 600 }}>
          {strings.GlobalLabels.Active}
        </span>
      );
    case 1:
      return (
        <span style={{ color: "#b37f27", fontWeight: 600 }}>
          {strings.GlobalLabels.Inactive}
        </span>
      );
    case 2:
      return (
        <span style={{ color: "#ab1111", fontWeight: 600 }}>
          {strings.GlobalLabels.Deleted}
        </span>
      );
    case -1:
      return (
        <span style={{ color: "#ab1111", fontWeight: 600 }}>
          {strings.GlobalLabels.Deleted}
        </span>
      );
  }
};

export const getStatus = (value: number) => {
  switch (value) {
    case 0:
      return (
        <span style={{ color: "#d9a61c", fontWeight: 700 }}>Khởi tạo</span>
      );
    case 1:
      return (
        <span style={{ color: "#2029a8", fontWeight: 700 }}>Đang xử lý</span>
      );
    case 2:
      return (
        <span style={{ color: "green", fontWeight: 700 }}>Hoàn thành</span>
      );
    case 3:
      return <span style={{ color: "red", fontWeight: 700 }}>Lỗi</span>;
    case 4:
      return <span style={{ color: "#730d34", fontWeight: 700 }}>Đã xóa</span>;
    case 5:
      return (
        <span style={{ color: "#c79712", fontWeight: 700 }}>Chờ xác nhận</span>
      );
    default:
      return "";
  }
};

export const statusForSimApplication = (value: number) => {
  switch (value) {
    case 10:
      return "Chờ thanh toán";
    case 11:
      return "Chờ giao hàng";
    case 12:
      return "Chờ nhận hàng";
    case 13:
      return "Giao hàng thành công";
    case 14:
      return "Đã hủy";
    case 15:
      return "Đã hòa mạng";
    case 16:
      return "Hòa mạng thất bại";
    default:
      return "";
  }
};

export const formatCurrency = (
  value: string | number,
  noStyle = false,
  needCurrency = true,
  currencyType?: CurrencyEnum,
) => {
  const parsedValue = typeof value === "string" ? Number(value) : value;

  return (
    <span
      style={
        !noStyle
          ? {
            fontWeight: 550,
            color: parsedValue > 0 ? "green" : "red",
          }
          : null
      }
    >
      {NumberFormat.currencyFormat(
        parsedValue,
        needCurrency,
        needCurrency ? currencyType : undefined,
      )}
    </span>
  );
};

export const paramsChecking = <T extends unknown>(
  value: T | undefined,
  type: "select" | "input",
) => {
  switch (type) {
    case "select":
      if (value === strings.GlobalLabels.All) {
        return undefined;
      } else if (typeof value === "number") {
        return value;
      } else if (isEmpty(value)) {
        return undefined;
      } else {
        return value;
      }
    case "input":
      return isEmpty(value) || isUndefined(value) ? undefined : value;
  }
};

export const avoidDuplicate = <T extends string | number | boolean | Object>(
  baseInfo: T,
  newInfo: typeof baseInfo,
) => {
  if (newInfo === baseInfo) {
    return undefined;
  } else {
    return newInfo;
  }
};

export const areEqual = (arr1: Array<unknown>, arr2: Array<unknown>) => {
  let N = arr1.length || 0;
  let M = arr2.length || 0;

  if (N !== M) return false;

  arr1.sort();
  arr2.sort();

  for (let i = 0; i < N; i++) if (arr1[i] !== arr2[i]) return false;
  return true;
};

export const downloadFile = async (
  fileUrl: string,
  title: string,
  extension?: string,
) => {
  if (!fileUrl) return;

  let finalFileName = title || "document";
  const ext = extension
    ? extension.replace(/^\./, "").trim()
    : fileUrl.split("?")[0].split("#")[0].split(".").pop()?.trim() || "";

  if (ext && !finalFileName.toLowerCase().endsWith(`.${ext.toLowerCase()}`)) {
    finalFileName = `${finalFileName}.${ext}`;
  }

  try {
    const response = await axios({
      method: "GET",
      url: fileUrl,
      responseType: "arraybuffer",
      headers: {
        Authorization: `Bearer ${DataFormat.getToken()}`,
      },
    });
    const contentType = String(
      response.headers["content-type"] || "application/octet-stream",
    );
    const blob = new Blob([response.data], { type: contentType });
    downloadFileFromBlob(blob, finalFileName);
  } catch (error) {
    console.error("Error downloading the file:", error);
    // Fallback: nếu axios bị lỗi (ví dụ CORS), tải trực tiếp qua link URL gốc
    const link = document.createElement("a");
    link.href = fileUrl;
    link.download = finalFileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
  }
};

export const downloadFileFromBlob = (blob: Blob, title: string) => {
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = title;
  document.body.appendChild(link);
  link.click();
  link.remove();
};

export const uppercaseFirstLetter = (str: string) => {
  return str?.charAt(0)?.toUpperCase() + str?.slice(1);
};

export const renderItem = (
  label: string,
  value: React.ReactNode,
  color = "black",
  copyValue = value,
) => {
  return (
    <div className="flex justify-between w-full my-2">
      <span className="font-medium min-w-[100px] opacity-70">{label}:</span>
      <span
        style={{
          color: color,
          textAlign: "right",
          whiteSpace: "pre-line",
          cursor: "context-menu",
        }}
        className={`font-medium ${getTextFromNode(value).includes(" ") ? "break-words" : "break-all"
          }`}
        onContextMenu={(e) => {
          e.preventDefault();
          copyToClipboard(copyValue);
        }}
        title={strings.GlobalMessage.RightClickCopy}
      >
        {value && value !== "-" && value !== "Invalid Date"
          ? value
          : strings.GlobalLabels.NoInfo}
      </span>
    </div>
  );
};

export const copyToClipboard = (value: any) => {
  const textToCopy = getTextFromNode(value);

  if (!textToCopy) return;

  if (navigator?.clipboard?.writeText) {
    navigator.clipboard
      .writeText(textToCopy)
      .then(() => {
        getMessage(
          `${strings.FileManagement.Placeholder.SavedValue}: ${textToCopy}`,
          4,
          "success",
        );
      })
      .catch((error) => {
        console.error("Clipboard error:", error);
      });
  } else {
    const textarea = document.createElement("textarea");
    textarea.value = textToCopy;
    document.body.appendChild(textarea);
    textarea.select();
    try {
      document.execCommand("copy");
      getMessage(
        `${strings.FileManagement.Placeholder.SavedValue}: ${textToCopy}`,
        4,
        "success",
      );
    } catch (err) {
      console.error("Fallback copy failed:", err);
    }
    document.body.removeChild(textarea);
  }
};

export const checkClipboardReadPermission = async () => {
  try {
    const result = await navigator.permissions.query({
      name: "clipboard-read",
    } as any);

    if (result.state === "granted") {
      return result.state;
    } else if (result.state === "denied") {
      return result.state;
    } else if (result.state === "prompt") {
      // Calling readText() is the only way to trigger the native browser
      // permission dialog — permissions.query() alone does not show it.
      // After the dialog closes the document loses focus, so writeText() will
      // also fail if called immediately. Return "just-granted" so the caller
      // can notify the user to click again on the next gesture.
      try {
        await navigator.clipboard.readText();
        return "just-granted";
      } catch {
        // User denied the prompt or the call was blocked.
        return "denied";
      }
    }

    return result.state;
  } catch (error) {
    console.error("Error checking clipboard permissions:", error);
    return "not-supported";
  }
};

export const combineImages = (contract: AppProfileResponseEntity) => {
  const images: AssetsEntity[] = [];

  // Gom tất cả files vào một mảng tạm để xử lý group
  const allFiles: (AssetsEntity & { phoneNumber?: string })[] = [];

  // Lấy từ businessCerts
  if (contract?.organization?.businessCerts?.length > 0) {
    contract.organization.businessCerts.forEach((item) => {
      allFiles.push({ ...item });
    });
  }

  // Lấy từ participants
  if (contract?.participants?.length > 0) {
    contract.participants.forEach((user: ParticipantEntity) => {
      user?.files?.forEach((file: AssetsEntity) => {
        allFiles.push({ ...file, phoneNumber: user.phoneNumber });
      });
    });
  }

  // Lấy từ file
  if (!isEmpty(contract?.file)) {
    allFiles.push({
      ...contract.file,
      phoneNumber: contract.participants[0]?.phoneNumber,
    });
  }

  // Group theo classify
  const grouped = allFiles.reduce(
    (acc, file) => {
      const key = file.classify;
      if (!acc[key]) acc[key] = [];
      acc[key].push(file);
      return acc;
    },
    {} as Record<number, (AssetsEntity & { phoneNumber?: string })[]>,
  );

  // Tạo label theo thứ tự trong mỗi nhóm
  Object.entries(grouped).forEach(([classify, files]) => {
    files.forEach((file, index) => {
      const titleBase = new AppProfileResponseEntity().getAssetsLabel(
        Number(classify),
      );
      const count = files.length;
      const label = `${titleBase} ${file?.phoneNumber ? `- ${file?.phoneNumber}` : ""
        } ${count > 1 ? `(${index + 1}/${count})` : ""}`;

      images.push({
        ...file,
        title: label,
      });
    });
  });

  return images;
};

export const parseNoteString = (
  note: string,
): Record<string, string | string[]> => {
  if (!note) return;
  const parts = note.split("|");
  const result: Record<string, string | string[]> = {};
  const products: string[] = [];

  for (const part of parts) {
    if (part.includes("=")) {
      const [key, value] = part.split("=");
      result[key] = value;
    } else if (part.trim() !== "") {
      products.push(part);
    }
  }

  if (products.length > 0) {
    result["product"] = products.length === 1 ? products[0] : products;
  }

  return result;
};

export const getTextFromNode = (node: any): string => {
  if (typeof node === "string" || typeof node === "number") {
    return String(node);
  }
  if (Array.isArray(node)) {
    return node.map(getTextFromNode).join("");
  }
  if (node?.props?.children) {
    return getTextFromNode(node.props.children);
  }
  return "";
};
